import { Amount } from '../../api/server/models/Amount';
import { ProviderEOBProviderAdjustment } from '../../api/server/models/ProviderEOBProviderAdjustment';
import { readX12Separators, x12Segments } from '../era-835/era-835';
import { providerAdjustmentCode, providerAdjustmentLabel } from './remit-codes';

/**
 * Provider-level adjustments (FE-163, crosswalk §2): money the payer takes from or adds to a payment that belongs to
 * no claim — an overpayment recovered, interest owed. They are why a remit's claims do not add up to its check.
 *
 * ## Sign
 *
 * In the 835 a PLB amount REDUCES the payment when positive: `WO` 125.00 takes $125 back, `L6` -3.42 adds $3.42
 * of interest. `effect` is the change to the payment, so it is the amount negated — what the page shows under
 * "Effect on payment" and sums in the balance line. EOBs follow the same sign rule (crosswalk §2).
 */
export interface IProviderAdjustment {
    /** PLB03-1 / the EOB type's X12 code: `WO`, `L6`. */
    code: string;
    label: string;
    /** PLB03-2 / `providerAdjustmentIdentifier` — the payer's reference for it. */
    reference?: string;
    /** PLB01 — the provider identifier the adjustment is reported against. ERA only. */
    provider?: string;
    /** PLB02 as `yyyy-MM-dd`. ERA only. */
    fiscalPeriod?: string;
    /** The change to the payment: negative takes money away. */
    effect: number;
}

export const amountOf = (amount: Amount | undefined): number => {
    const value = Number(amount?.amount);
    return Number.isFinite(value) ? value : 0;
};

const isoDay = (ccyymmdd: string | undefined): string | undefined =>
    ccyymmdd && /^\d{8}$/.test(ccyymmdd) ? `${ccyymmdd.slice(0, 4)}-${ccyymmdd.slice(4, 6)}-${ccyymmdd.slice(6, 8)}` : undefined;

/**
 * Every PLB adjustment in an ERA's stored 835. A PLB segment carries up to six code/amount pairs after its provider
 * and fiscal period: `PLB*1588901532*20261231*WO:265687690300*125*L6:INT0913*-3.42~`. Undefined when the text is
 * not an interchange — the caller says the adjustments could not be read, never that there are none.
 */
export const eraProviderAdjustments = (x12: string | undefined): IProviderAdjustment[] | undefined => {
    const separators = x12 ? readX12Separators(x12) : undefined;
    if (!x12 || !separators) {
        return undefined;
    }
    const adjustments: IProviderAdjustment[] = [];
    for (const elements of x12Segments(x12)) {
        if (elements[0] !== 'PLB') {
            continue;
        }
        const [, provider, fiscalPeriod, ...pairs] = elements;
        for (let index = 0; index + 1 < pairs.length; index += 2) {
            const [code, reference] = (pairs[index] || '').split(separators.component);
            const amount = Number(pairs[index + 1]);
            if (!code || !Number.isFinite(amount)) {
                continue;
            }
            adjustments.push({
                code,
                label: providerAdjustmentLabel(code),
                reference: reference || undefined,
                provider: provider || undefined,
                fiscalPeriod: isoDay(fiscalPeriod),
                effect: amount === 0 ? 0 : -amount,
            });
        }
    }
    return adjustments;
};

/** An EOB's provider adjustments, keyed and signed like an ERA's. */
export const eobProviderAdjustments = (adjustments: ProviderEOBProviderAdjustment[] | undefined): IProviderAdjustment[] =>
    (adjustments || []).map(adjustment => {
        const code = providerAdjustmentCode(adjustment.providerAdjustmentCodeType);
        const amount = amountOf(adjustment.providerAdjustmentAmount);
        return {
            code,
            label: providerAdjustmentLabel(code),
            reference: adjustment.providerAdjustmentIdentifier || undefined,
            effect: amount === 0 ? 0 : -amount,
        };
    });
